// src/pages/News.jsx
import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import LoadingSpinner from '../components/LoadingSpinner';

const News = () => {
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    featured: false,
    imageUrl: '',
    pdfUrl: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [pdfPreview, setPdfPreview] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFeatured, setFilterFeatured] = useState('all');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [currentTime, setCurrentTime] = useState('');

  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }));
    };
    updateClock();
    const clockInterval = setInterval(updateClock, 1000);

    const q = query(collection(db, 'news'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const newsData = [];
      snapshot.forEach((doc) => {
        newsData.push({ id: doc.id, ...doc.data() });
      });
      setNews(newsData);
      setLoading(false);
    });

    loadCategories();

    return () => {
      clearInterval(clockInterval);
      unsubscribe();
    };
  }, []);

  const loadCategories = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'news_categories'));
      const catData = [];
      snapshot.forEach((doc) => {
        catData.push({ id: doc.id, name: doc.data().name });
      });
      setCategories(catData);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      description: '',
      featured: false,
      imageUrl: '',
      pdfUrl: ''
    });
    setImageFile(null);
    setPdfFile(null);
    setImagePreview('');
    setPdfPreview('');
    setEditingNews(null);
    setUploadProgress(0);
    setIsUploading(false);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEditModal = (item) => {
    setEditingNews(item);
    setFormData({
      title: item.title || '',
      category: item.category || '',
      description: item.description || '',
      featured: item.featured || false,
      imageUrl: item.imageUrl || '',
      pdfUrl: item.pdfUrl || ''
    });
    if (item.imageUrl) setImagePreview(item.imageUrl);
    if (item.pdfUrl) setPdfPreview(item.pdfUrl);
    setModalOpen(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please upload a PDF file');
        return;
      }
      setPdfFile(file);
      setPdfPreview(file.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsUploading(true);

    try {
      let imageUrl = formData.imageUrl;
      let pdfUrl = formData.pdfUrl;

      if (imageFile) {
        const storageRef = ref(storage, `news/images/${Date.now()}_${imageFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      if (pdfFile) {
        const storageRef = ref(storage, `news/pdfs/${Date.now()}_${pdfFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, pdfFile);
        await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              pdfUrl = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      const newsData = {
        title: formData.title,
        category: formData.category,
        description: formData.description,
        featured: formData.featured,
        imageUrl,
        pdfUrl,
        updatedAt: new Date()
      };

      if (editingNews) {
        if (formData.imageUrl && imageFile && formData.imageUrl !== imageUrl) {
          try { await deleteObject(ref(storage, formData.imageUrl)); } catch (e) {}
        }
        if (formData.pdfUrl && pdfFile && formData.pdfUrl !== pdfUrl) {
          try { await deleteObject(ref(storage, formData.pdfUrl)); } catch (e) {}
        }
        await updateDoc(doc(db, 'news', editingNews.id), newsData);
        alert('News updated successfully!');
      } else {
        newsData.createdAt = new Date();
        await addDoc(collection(db, 'news'), newsData);
        alert('News created successfully!');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving news:', error);
      alert('Error saving news: ' + error.message);
    } finally {
      setLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id, imageUrl, pdfUrl) => {
    if (!window.confirm('Delete this news permanently?')) return;
    
    setLoading(true);
    try {
      if (imageUrl) {
        try { await deleteObject(ref(storage, imageUrl)); } catch (e) {}
      }
      if (pdfUrl) {
        try { await deleteObject(ref(storage, pdfUrl)); } catch (e) {}
      }
      await deleteDoc(doc(db, 'news', id));
      alert('News deleted successfully!');
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('Error deleting news: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    
    try {
      await addDoc(collection(db, 'news_categories'), {
        name: newCategoryName.trim(),
        createdAt: new Date()
      });
      setNewCategoryName('');
      setCategoryModalOpen(false);
      await loadCategories();
      alert('Category created successfully!');
    } catch (error) {
      console.error('Error adding category:', error);
      alert('Error adding category: ' + error.message);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setNewCategoryName('');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  const getFilteredNews = () => {
    let filtered = [...news];
    if (filterCategory !== 'all') {
      filtered = filtered.filter(n => n.category === filterCategory);
    }
    if (filterFeatured === 'featured') {
      filtered = filtered.filter(n => n.featured === true);
    }
    return filtered;
  };

  const styles = `
    .news-page {
      animation: structuralSlideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    .news-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
      gap: 28px;
    }

    .news-card-item {
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.6);
      box-shadow: 0 8px 32px 0 rgba(14, 165, 233, 0.06);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    .news-card-item:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.65);
      box-shadow: 0 16px 36px 0 rgba(14, 165, 233, 0.14);
      border-color: rgba(14, 165, 233, 0.3);
    }

    .news-image-wrapper {
      height: 220px;
      background: linear-gradient(135deg, #0f172a, #334155);
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      position: relative;
    }

    .news-actual-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .news-image-placeholder {
      font-size: 44px;
      color: rgba(255, 255, 255, 0.5);
    }

    .news-info-box {
      padding: 24px;
      display: flex;
      flex-direction: column;
      flex: 1;
    }

    .news-badge-row {
      display: flex;
      gap: 6px;
      margin-bottom: 12px;
      flex-wrap: wrap;
    }

    .news-card-category {
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 0.72rem;
      font-weight: 700;
      background: rgba(14, 165, 233, 0.12);
      color: #0ea5e9;
      border: 1px solid rgba(14, 165, 233, 0.2);
    }

    .news-card-featured-badge {
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 0.72rem;
      font-weight: 700;
      background: rgba(245, 158, 11, 0.12);
      color: #f59e0b;
      border: 1px solid rgba(245, 158, 11, 0.2);
    }

    .pdf-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 10px;
      border-radius: 6px;
      font-size: 0.7rem;
      font-weight: 700;
      background: rgba(16, 185, 129, 0.12);
      color: #10b981;
      border: 1px solid rgba(16, 185, 129, 0.2);
    }

    .news-card-title {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.25rem;
      font-weight: 700;
      margin-bottom: 10px;
      color: #0f172a;
    }

    .news-card-description {
      color: #334155;
      font-size: 0.88rem;
      margin-bottom: 20px;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      line-height: 1.6;
    }

    .news-card-meta {
      font-size: 0.78rem;
      color: #64748b;
      display: flex;
      justify-content: space-between;
      padding-top: 12px;
      border-top: 1px solid rgba(14, 165, 233, 0.1);
    }

    .news-card-actions {
      display: flex;
      gap: 10px;
      padding-top: 16px;
      margin-top: 12px;
    }

    .btn-pdf {
      background: rgba(16, 185, 129, 0.1);
      border: 1px solid rgba(16, 185, 129, 0.3);
      color: #10b981;
    }

    .btn-pdf:hover {
      background: #10b981;
      color: white;
    }

    .btn-row-action {
      flex: 1;
      padding: 10px 14px;
      border-radius: 8px;
      font-weight: 700;
      font-size: 0.8rem;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: all 0.2s;
      border: none;
    }

    .btn-row-edit {
      background: rgba(14, 165, 233, 0.12);
      border: 1px solid rgba(14, 165, 233, 0.3);
      color: #0ea5e9;
    }

    .btn-row-edit:hover {
      background: #0ea5e9;
      color: white;
    }

    .btn-row-delete {
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      color: #f43f5e;
    }

    .btn-row-delete:hover {
      background: #f43f5e;
      color: white;
    }

    .empty-state {
      text-align: center;
      padding: 60px 20px;
      background: rgba(255, 255, 255, 0.45);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.6);
      grid-column: 1/-1;
    }

    .empty-state i {
      font-size: 54px;
      color: #64748b;
      margin-bottom: 16px;
      display: block;
    }

    .empty-state h3 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.2rem;
      margin-bottom: 6px;
      color: #0f172a;
    }

    .empty-state p {
      color: #64748b;
      font-size: 0.88rem;
      margin-bottom: 20px;
    }

    .btn-action-trigger {
      padding: 12px 24px;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      box-shadow: 0 4px 15px rgba(14, 165, 233, 0.2);
    }

    .btn-action-trigger:hover {
      transform: translateY(-3px);
    }

    .btn-blue {
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
    }

    .btn-blue:hover {
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-purple {
      background: linear-gradient(135deg, #8b5cf6, #6d28d9);
      color: white;
    }

    .btn-purple:hover {
      box-shadow: 0 10px 25px rgba(139, 92, 246, 0.4);
    }

    .filter-select {
      padding: 10px 20px;
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      outline: none;
      transition: all 0.2s;
    }

    .filter-select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .management-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      background: rgba(255, 255, 255, 0.3);
      border: 1px solid rgba(255, 255, 255, 0.6);
      padding: 16px 24px;
      border-radius: 16px;
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      flex-wrap: wrap;
      gap: 16px;
    }

    /* Modal Styles - Positioned at Top */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      min-height: 100vh;
      background: rgba(15, 23, 42, 0.7);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      z-index: 99999;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding: 40px 20px 80px 20px;
      overflow-y: auto;
      animation: fadeIn 0.3s ease;
    }

    .modal-viewport-box {
      background: rgba(255, 255, 255, 0.98);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(255, 255, 255, 0.6);
      border-radius: 24px;
      padding: 40px;
      width: 100%;
      max-width: 700px;
      max-height: none;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideDown 0.3s ease;
      margin-bottom: 40px;
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .modal-viewport-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(14, 165, 233, 0.1);
    }

    .modal-viewport-header h2 {
      font-family: 'Space Grotesk', sans-serif;
      font-size: 1.4rem;
      color: #0f172a;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .modal-viewport-header h2 i {
      color: #0ea5e9;
    }

    .modal-close-btn {
      width: 40px;
      height: 40px;
      background: rgba(244, 63, 94, 0.1);
      border: 1px solid rgba(244, 63, 94, 0.25);
      border-radius: 12px;
      color: #f43f5e;
      cursor: pointer;
      font-size: 1.2rem;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .modal-close-btn:hover {
      background: #f43f5e;
      color: white;
      transform: rotate(90deg);
    }

    .form-group {
      margin-bottom: 20px;
    }

    .form-group label {
      display: block;
      margin-bottom: 8px;
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
    }

    .form-group label .required {
      color: #f43f5e;
      margin-left: 4px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 12px 16px;
      border: 2px solid rgba(14, 165, 233, 0.15);
      border-radius: 12px;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.6);
      color: #0f172a;
      outline: none;
      transition: all 0.3s ease;
      font-family: inherit;
    }

    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      border-color: #0ea5e9;
      background: #ffffff;
      box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1);
    }

    .form-group textarea {
      resize: vertical;
      min-height: 100px;
    }

    .file-upload-wrapper {
      position: relative;
      border: 2px dashed rgba(14, 165, 233, 0.3);
      border-radius: 12px;
      padding: 16px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: rgba(255, 255, 255, 0.3);
    }

    .file-upload-wrapper:hover {
      border-color: #0ea5e9;
      background: rgba(255, 255, 255, 0.5);
    }

    .file-upload-wrapper input[type="file"] {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      cursor: pointer;
    }

    .file-upload-wrapper .upload-icon {
      font-size: 1.5rem;
      color: #0ea5e9;
      margin-bottom: 4px;
      display: block;
    }

    .file-upload-wrapper .upload-text {
      color: #64748b;
      font-size: 0.8rem;
      font-weight: 600;
    }

    .file-upload-wrapper .upload-text span {
      color: #0ea5e9;
      font-weight: 700;
    }

    .file-preview-wrapper {
      margin-top: 8px;
      padding: 8px 12px;
      background: rgba(14, 165, 233, 0.05);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border: 1px solid rgba(14, 165, 233, 0.1);
    }

    .file-preview-wrapper .file-info {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .file-preview-wrapper .file-info i {
      color: #f43f5e;
      font-size: 1.2rem;
    }

    .file-preview-wrapper .file-info .file-name {
      font-weight: 600;
      font-size: 0.85rem;
      color: #0f172a;
    }

    .file-preview-wrapper .remove-file {
      background: rgba(244, 63, 94, 0.1);
      border: none;
      border-radius: 6px;
      color: #f43f5e;
      padding: 4px 10px;
      cursor: pointer;
      font-size: 0.75rem;
      font-weight: 600;
      transition: all 0.2s;
    }

    .file-preview-wrapper .remove-file:hover {
      background: #f43f5e;
      color: white;
    }

    .image-preview-wrapper {
      margin-top: 12px;
      position: relative;
      display: inline-block;
      border-radius: 12px;
      overflow: hidden;
      border: 2px solid rgba(14, 165, 233, 0.2);
    }

    .image-preview-wrapper img {
      width: 120px;
      height: 120px;
      object-fit: cover;
      display: block;
    }

    .image-preview-wrapper .remove-image {
      position: absolute;
      top: 4px;
      right: 4px;
      background: rgba(244, 63, 94, 0.9);
      color: white;
      border: none;
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      font-size: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
    }

    .image-preview-wrapper .remove-image:hover {
      transform: scale(1.1);
      background: #f43f5e;
    }

    .upload-progress {
      margin-top: 12px;
      background: rgba(14, 165, 233, 0.1);
      border-radius: 8px;
      height: 6px;
      overflow: hidden;
    }

    .upload-progress .progress-bar {
      height: 100%;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      transition: width 0.3s ease;
      border-radius: 8px;
    }

    .checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 10px;
      cursor: pointer;
      padding: 4px 0;
    }

    .checkbox-wrapper input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: #0ea5e9;
      cursor: pointer;
    }

    .checkbox-wrapper label {
      font-weight: 700;
      font-size: 0.85rem;
      color: #334155;
      cursor: pointer;
      margin: 0;
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 2px solid rgba(14, 165, 233, 0.1);
    }

    .btn-cancel {
      padding: 10px 24px;
      background: rgba(255, 255, 255, 0.4);
      border: 2px solid rgba(14, 165, 233, 0.2);
      border-radius: 12px;
      color: #334155;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
    }

    .btn-cancel:hover {
      background: #ffffff;
      border-color: #334155;
      transform: translateY(-2px);
    }

    .btn-submit {
      padding: 10px 28px;
      background: linear-gradient(135deg, #0ea5e9, #0284c7);
      color: white;
      border: none;
      border-radius: 12px;
      cursor: pointer;
      font-weight: 700;
      font-size: 0.9rem;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .btn-submit:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(14, 165, 233, 0.4);
    }

    .btn-submit:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-submit .spinner {
      width: 18px;
      height: 18px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }

    /* Category Modal */
    .category-modal-box {
      max-width: 450px;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @media (max-width: 768px) {
      .news-grid {
        grid-template-columns: 1fr;
      }
      .management-toolbar {
        flex-direction: column;
        align-items: stretch;
      }
      .management-toolbar > div {
        justify-content: center;
      }
      .modal-viewport-box {
        padding: 24px;
        margin: 10px;
      }
      .modal-viewport-header h2 {
        font-size: 1.2rem;
      }
      .modal-footer {
        flex-direction: column;
      }
      .modal-footer button {
        width: 100%;
        justify-content: center;
      }
    }
  `;

  if (loading && news.length === 0) {
    return <LoadingSpinner />;
  }

  const filteredNews = getFilteredNews();

  return (
    <>
      <style>{styles}</style>
      <div className="news-page">
        <div className="welcome-hero">
          <h1>📰 News Management</h1>
          <p>Create news articles with images and PDF attachments</p>
          <div className="hero-meta-date">
            <i className="far fa-clock"></i> {currentTime}
          </div>
        </div>

        <div className="management-toolbar">
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <select 
              className="filter-select" 
              value={filterCategory} 
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
            <select 
              className="filter-select" 
              value={filterFeatured} 
              onChange={(e) => setFilterFeatured(e.target.value)}
            >
              <option value="all">All News</option>
              <option value="featured">Featured Only</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button className="btn-action-trigger btn-purple" onClick={() => setCategoryModalOpen(true)}>
              <i className="fas fa-tag"></i> New Category
            </button>
            <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
              <i className="fas fa-plus"></i> Add News
            </button>
          </div>
        </div>

        <div className="news-grid">
          {filteredNews.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-newspaper"></i>
              <h3>No News Articles Found</h3>
              <p>Click "Add News" to create your first article.</p>
              <button className="btn-action-trigger btn-blue" onClick={openCreateModal}>
                <i className="fas fa-plus"></i> Create First Article
              </button>
            </div>
          ) : (
            filteredNews.map((item) => (
              <div className="news-card-item" key={item.id}>
                <div className="news-image-wrapper">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} className="news-actual-img" alt={item.title} />
                  ) : (
                    <i className="fas fa-newspaper news-image-placeholder"></i>
                  )}
                </div>
                <div className="news-info-box">
                  <div className="news-badge-row">
                    <span className="news-card-category">{item.category || 'General'}</span>
                    {item.featured && <span className="news-card-featured-badge">⭐ Featured</span>}
                    {item.pdfUrl && (
                      <span className="pdf-badge">
                        <i className="fas fa-file-pdf"></i> PDF Attached
                      </span>
                    )}
                  </div>
                  <h3 className="news-card-title">{item.title}</h3>
                  <p className="news-card-description">{item.description}</p>
                  <div className="news-card-meta">
                    <span><i className="far fa-calendar"></i> {formatDate(item.createdAt)}</span>
                  </div>
                  <div className="news-card-actions">
                    {item.pdfUrl && (
                      <button className="btn-row-action btn-pdf" onClick={() => window.open(item.pdfUrl, '_blank')}>
                        <i className="fas fa-download"></i> PDF
                      </button>
                    )}
                    <button className="btn-row-action btn-row-edit" onClick={() => openEditModal(item)}>
                      <i className="fas fa-edit"></i> Edit
                    </button>
                    <button className="btn-row-action btn-row-delete" onClick={() => handleDelete(item.id, item.imageUrl, item.pdfUrl)}>
                      <i className="fas fa-trash"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* News Modal - Positioned at Top */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-viewport-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className={`fas ${editingNews ? 'fa-edit' : 'fa-plus-circle'}`}></i>
                  {editingNews ? 'Edit News Article' : 'Create New News'}
                </h2>
                <button className="modal-close-btn" onClick={closeModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Title <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter news title"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category <span className="required">*</span></label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Description <span className="required">*</span></label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="4"
                    placeholder="Enter news description..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label>📷 Featured Image (JPG/PNG)</label>
                  <div className="file-upload-wrapper">
                    <i className="fas fa-cloud-upload-alt upload-icon"></i>
                    <div className="upload-text">
                      Drag & drop or <span>browse</span> to upload image
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </div>
                  {imagePreview && (
                    <div className="image-preview-wrapper">
                      <img src={imagePreview} alt="Preview" />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={() => {
                          setImagePreview('');
                          setImageFile(null);
                          setFormData({ ...formData, imageUrl: '' });
                        }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>📄 PDF Attachment (PDF only)</label>
                  <div className="file-upload-wrapper">
                    <i className="fas fa-file-pdf upload-icon" style={{ color: '#f43f5e' }}></i>
                    <div className="upload-text">
                      Drag & drop or <span>browse</span> to upload PDF
                    </div>
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handlePdfChange}
                    />
                  </div>
                  {pdfPreview && (
                    <div className="file-preview-wrapper">
                      <div className="file-info">
                        <i className="fas fa-file-pdf"></i>
                        <span className="file-name">{pdfPreview}</span>
                      </div>
                      <button
                        type="button"
                        className="remove-file"
                        onClick={() => {
                          setPdfPreview('');
                          setPdfFile(null);
                          setFormData({ ...formData, pdfUrl: '' });
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    id="featuredCheckbox"
                  />
                  <label htmlFor="featuredCheckbox">⭐ Feature this news</label>
                </div>

                {isUploading && uploadProgress > 0 && (
                  <div className="upload-progress" style={{ marginTop: '16px' }}>
                    <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <span className="spinner"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save"></i>
                        {editingNews ? 'Update News' : 'Create News'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Category Modal - Positioned at Top */}
        {categoryModalOpen && (
          <div className="modal-overlay" onClick={closeCategoryModal}>
            <div className="modal-viewport-box category-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="modal-viewport-header">
                <h2>
                  <i className="fas fa-tag" style={{ color: '#8b5cf6' }}></i>
                  Create Category
                </h2>
                <button className="modal-close-btn" onClick={closeCategoryModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleAddCategory}>
                <div className="form-group">
                  <label>Category Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="e.g., Academics, Events, Campus Life"
                    required
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeCategoryModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                  <button type="submit" className="btn-submit" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>
                    <i className="fas fa-plus"></i> Create Category
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default News;